import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Button,
  Form,
  Badge,
  Table,
  Modal,
  Spinner,
  Alert,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  IconUsers,
  IconPlus,
  IconRefresh,
  IconEdit,
  IconTrash,
  IconCircle,
  IconSettings,
  IconUserCheck,
  IconClock,
} from "@tabler/icons-react";
import UserContext from "../../../contexts/UserContext";
import { 
  getAgents,
  createAgent,
  updateAgent,
  updateAgentStatus
} from "../../../services/SupportServices";

const AgentManagement = ({ config }) => {
  const { user } = useContext(UserContext);
  
  // Estados principais
  const [agents, setAgents] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Estados de UI
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  
  // Estados do formulário
  const [newAgentForm, setNewAgentForm] = useState({
    user: "",
    can_handle_tickets: true,
    can_handle_chats: true,
    max_concurrent_chats: 3,
    specialties: []
  });
  
  const [editForm, setEditForm] = useState({
    can_handle_tickets: true,
    can_handle_chats: true,
    max_concurrent_chats: 3,
    specialties: []
  });
  
  const [newSpecialty, setNewSpecialty] = useState("");

  useEffect(() => {
    loadAgents();
    loadUsers();
  }, []);

  // ==========================================
  // FUNÇÕES DE API
  // ==========================================

  const loadAgents = async () => {
    try {
      setIsLoading(true);
      
      const result = await getAgents();
      
      if (result.success) {
        setAgents(result.data);
      } else if (result.isApiUnavailable) {
        // API não está disponível, não mostrar erro
        setAgents([]);
      } else {
        console.error("Erro ao carregar agentes:", result.error);
        setAgents([]);
      }
    } catch (error) {
      console.warn("Erro inesperado ao carregar agentes:", error.message);
      setAgents([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      // Para simplificar, vamos usar dados mockados por enquanto
      // até a API de usuários estar disponível
      setUsers([]);
    } catch (error) {
      console.warn("Erro inesperado ao carregar usuários:", error.message);
      setUsers([]);
    }
  };

  const createAgentHandler = async () => {
    try {
      setIsCreating(true);
      
      const result = await createAgent(newAgentForm);
      
      if (result.success) {
        setAgents(prev => [result.data, ...prev]);
        setShowCreateModal(false);
        resetNewAgentForm();
      } else if (result.isApiUnavailable) {
        console.warn("API de agentes não está disponível");
      } else {
        console.error("Erro ao criar agente:", result.error);
      }
    } catch (error) {
      console.warn("Erro inesperado ao criar agente:", error.message);
    } finally {
      setIsCreating(false);
    }
  };

  const updateAgentHandler = async (agentId) => {
    try {
      setIsUpdating(true);
      
      const result = await updateAgent(agentId, editForm);
      
      if (result.success) {
        setAgents(prev => prev.map(agent => 
          agent.id === agentId ? { ...agent, ...result.data } : agent
        ));
        setShowEditModal(false);
      } else if (result.isApiUnavailable) {
        console.warn("API de agentes não está disponível");
      } else {
        console.error("Erro ao atualizar agente:", result.error);
      }
    } catch (error) {
      console.warn("Erro inesperado ao atualizar agente:", error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteAgentHandler = async (agentId) => {
    try {
      setIsUpdating(true);
      
      // Por enquanto vamos simular a exclusão, até a API estar pronta
      setAgents(prev => prev.filter(agent => agent.id !== agentId));
      setShowDeleteModal(false);
    } catch (error) {
      console.warn("Erro inesperado ao remover agente:", error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const updateAgentStatusHandler = async (agentId, status) => {
    try {
      const result = await updateAgentStatus(agentId, status);
      
      if (result.success) {
        loadAgents();
      } else if (result.isApiUnavailable) {
        console.warn("API de agentes não está disponível");
      } else {
        console.error("Erro ao atualizar status:", result.error);
      }
    } catch (error) {
      console.warn("Erro inesperado ao atualizar status:", error.message);
    }
  };

  // ==========================================
  // FUNÇÕES AUXILIARES
  // ==========================================

  const resetNewAgentForm = () => {
    setNewAgentForm({
      user: "",
      can_handle_tickets: true,
      can_handle_chats: true,
      max_concurrent_chats: 3,
      specialties: []
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      online: "success",
      busy: "warning",
      away: "secondary",
      offline: "danger"
    };
    
    const labels = {
      online: "Online",
      busy: "Ocupado",
      away: "Ausente",
      offline: "Offline"
    };
    
    return (
      <Badge bg={variants[status] || "secondary"} className="d-flex align-items-center gap-1">
        <IconCircle size={8} fill="currentColor" />
        {labels[status] || status}
      </Badge>
    );
  };

  const getStatusIcon = (status) => {
    const colors = {
      online: "#28a745",
      busy: "#ffc107",
      away: "#6c757d",
      offline: "#dc3545"
    };
    
    return <IconCircle size={12} fill={colors[status] || colors.offline} color={colors[status] || colors.offline} />;
  };

  const addSpecialty = (specialties, setSpecialties) => {
    if (newSpecialty.trim() && !specialties.includes(newSpecialty.trim())) {
      setSpecialties([...specialties, newSpecialty.trim()]);
      setNewSpecialty("");
    }
  };

  const removeSpecialty = (specialtyToRemove, specialties, setSpecialties) => {
    setSpecialties(specialties.filter(specialty => specialty !== specialtyToRemove));
  };

  // ==========================================
  // RENDERIZAÇÃO
  // ==========================================

  if (!config || !config.is_active) {
    return (
      <Alert variant="warning">
        <h5>Sistema de Agentes Inativo</h5>
        <p>Configure e ative o sistema de suporte para gerenciar agentes.</p>
      </Alert>
    );
  }

  return (
    <div>
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <IconUsers className="me-2" />
              Gerenciamento de Agentes
            </h5>

            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={loadAgents}
                disabled={isLoading}
              >
                <IconRefresh size={16} />
              </Button>

              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowCreateModal(true)}
              >
                <IconPlus size={16} className="me-1" />
                Novo Agente
              </Button>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Carregando agentes...</p>
            </div>
          ) : agents.length === 0 ? (
            <div className="text-center py-4">
              <IconUsers size={48} className="text-muted mb-3" />
              <h5>Nenhum agente cadastrado</h5>
              <p className="text-muted">
                Adicione agentes para gerenciar o atendimento
              </p>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Agente</th>
                  <th>Status</th>
                  <th>Capacidades</th>
                  <th>Chats Simultâneos</th>
                  <th>Chats Ativos</th>
                  <th>Especialidades</th>
                  <th>Última Atividade</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((agent) => (
                  <tr key={agent.id}>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div className="position-relative">
                          {getStatusIcon(agent.status)}
                        </div>
                        <div>
                          <div className="fw-semibold">{agent.user_name}</div>
                          <small className="text-muted">{agent.email}</small>
                        </div>
                      </div>
                    </td>
                    <td>{getStatusBadge(agent.status)}</td>
                    <td>
                      <div className="d-flex gap-1">
                        {agent.can_handle_tickets && (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Pode atender tickets</Tooltip>}
                          >
                            <Badge bg="primary" className="small">
                              T
                            </Badge>
                          </OverlayTrigger>
                        )}
                        {agent.can_handle_chats && (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Pode atender chats</Tooltip>}
                          >
                            <Badge bg="info" className="small">
                              C
                            </Badge>
                          </OverlayTrigger>
                        )}
                      </div>
                    </td>
                    <td>
                      <Badge bg="secondary">{agent.max_concurrent_chats}</Badge>
                    </td>
                    <td>
                      <Badge
                        bg={agent.current_chat_count > 0 ? "success" : "light"}
                        text={agent.current_chat_count > 0 ? "white" : "dark"}
                      >
                        {agent.current_chat_count || 0}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-1 flex-wrap">
                        {agent.specialties?.map((specialty) => (
                          <Badge key={specialty} className="small">
                            {specialty}
                          </Badge>
                        ))}
                        {!agent.specialties?.length && (
                          <small className="text-muted">Nenhuma</small>
                        )}
                      </div>
                    </td>
                    <td>
                      <small className="text-muted">
                        <IconClock size={12} className="me-1" />
                        {agent.last_activity
                          ? new Date(agent.last_activity).toLocaleString()
                          : "Nunca"}
                      </small>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Editar agente</Tooltip>}
                        >
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => {
                              setSelectedAgent(agent);
                              setEditForm({
                                can_handle_tickets: agent.can_handle_tickets,
                                can_handle_chats: agent.can_handle_chats,
                                max_concurrent_chats:
                                  agent.max_concurrent_chats,
                                specialties: agent.specialties || [],
                              });
                              setShowEditModal(true);
                            }}
                          >
                            <IconEdit size={14} />
                          </Button>
                        </OverlayTrigger>

                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Remover agente</Tooltip>}
                        >
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => {
                              setSelectedAgent(agent);
                              setShowDeleteModal(true);
                            }}
                          >
                            <IconTrash size={14} />
                          </Button>
                        </OverlayTrigger>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal para Criar Agente */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Novo Agente de Suporte</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Usuário *</Form.Label>
              <Form.Select
                value={newAgentForm.user}
                onChange={(e) =>
                  setNewAgentForm((prev) => ({
                    ...prev,
                    user: e.target.value,
                  }))
                }
              >
                <option value="">Selecione um usuário...</option>
                {users
                  .filter((u) => !agents.find((a) => a.user === u.id))
                  .map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="can_handle_tickets"
                    label="Pode atender tickets"
                    checked={newAgentForm.can_handle_tickets}
                    onChange={(e) =>
                      setNewAgentForm((prev) => ({
                        ...prev,
                        can_handle_tickets: e.target.checked,
                      }))
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="can_handle_chats"
                    label="Pode atender chats"
                    checked={newAgentForm.can_handle_chats}
                    onChange={(e) =>
                      setNewAgentForm((prev) => ({
                        ...prev,
                        can_handle_chats: e.target.checked,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Máximo de Chats Simultâneos</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="10"
                value={newAgentForm.max_concurrent_chats}
                onChange={(e) =>
                  setNewAgentForm((prev) => ({
                    ...prev,
                    max_concurrent_chats: parseInt(e.target.value) || 1,
                  }))
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Especialidades</Form.Label>
              <div className="d-flex gap-2 mb-2">
                <Form.Control
                  type="text"
                  placeholder="Digite uma especialidade"
                  value={newSpecialty}
                  onChange={(e) => setNewSpecialty(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addSpecialty(newAgentForm.specialties, (specialties) =>
                        setNewAgentForm((prev) => ({ ...prev, specialties }))
                      );
                    }
                  }}
                />
                <Button
                  variant="outline-primary"
                  onClick={() =>
                    addSpecialty(newAgentForm.specialties, (specialties) =>
                      setNewAgentForm((prev) => ({ ...prev, specialties }))
                    )
                  }
                  disabled={!newSpecialty.trim()}
                >
                  <IconPlus size={16} />
                </Button>
              </div>
              <div className="d-flex gap-1 flex-wrap">
                {newAgentForm.specialties.map((specialty) => (
                  <Badge
                    key={specialty}
                    bg="secondary"
                    className="d-flex align-items-center gap-1 cursor-pointer"
                    onClick={() =>
                      removeSpecialty(
                        specialty,
                        newAgentForm.specialties,
                        (specialties) =>
                          setNewAgentForm((prev) => ({ ...prev, specialties }))
                      )
                    }
                  >
                    {specialty}
                    <span>×</span>
                  </Badge>
                ))}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={createAgentHandler}
            disabled={!newAgentForm.user || isCreating}
          >
            {isCreating && <Spinner size="sm" className="me-2" />}
            Criar Agente
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para Editar Agente */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Editar Agente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="edit_can_handle_tickets"
                    label="Pode atender tickets"
                    checked={editForm.can_handle_tickets}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        can_handle_tickets: e.target.checked,
                      }))
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="edit_can_handle_chats"
                    label="Pode atender chats"
                    checked={editForm.can_handle_chats}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        can_handle_chats: e.target.checked,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Máximo de Chats Simultâneos</Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="10"
                value={editForm.max_concurrent_chats}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    max_concurrent_chats: parseInt(e.target.value) || 1,
                  }))
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Especialidades</Form.Label>
              <div className="d-flex gap-2 mb-2">
                <Form.Control
                  type="text"
                  placeholder="Digite uma especialidade"
                  value={newSpecialty}
                  onChange={(e) => setNewSpecialty(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addSpecialty(editForm.specialties, (specialties) =>
                        setEditForm((prev) => ({ ...prev, specialties }))
                      );
                    }
                  }}
                />
                <Button
                  variant="outline-primary"
                  onClick={() =>
                    addSpecialty(editForm.specialties, (specialties) =>
                      setEditForm((prev) => ({ ...prev, specialties }))
                    )
                  }
                  disabled={!newSpecialty.trim()}
                >
                  <IconPlus size={16} />
                </Button>
              </div>
              <div className="d-flex gap-1 flex-wrap">
                {editForm.specialties.map((specialty) => (
                  <Badge
                    key={specialty}
                    bg="secondary"
                    className="d-flex align-items-center gap-1 cursor-pointer"
                    onClick={() =>
                      removeSpecialty(
                        specialty,
                        editForm.specialties,
                        (specialties) =>
                          setEditForm((prev) => ({ ...prev, specialties }))
                      )
                    }
                  >
                    {specialty}
                    <span>×</span>
                  </Badge>
                ))}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => updateAgentHandler(selectedAgent.id)}
            disabled={isUpdating}
          >
            {isUpdating && <Spinner size="sm" className="me-2" />}
            Salvar Alterações
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para Confirmar Exclusão */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Exclusão</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Tem certeza que deseja remover{" "}
            <strong>{selectedAgent?.user_name}</strong>
            como agente de suporte?
          </p>
          <Alert variant="warning">
            <small>
              Esta ação não pode ser desfeita. O usuário perderá o acesso ao
              sistema de suporte.
            </small>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteAgentHandler(selectedAgent.id)}
            disabled={isUpdating}
          >
            {isUpdating && <Spinner size="sm" className="me-2" />}
            Remover Agente
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .cursor-pointer {
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default AgentManagement;